import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";

import PackingList from "../components/PackingList";
import { getTitleLinkMissing } from "../selectors/packing";
import { extractCenterId } from "../utils/center";
import { isMissingQueueEnforced } from "../utils/permissions";
import { api } from "../api";

const SHOW_OBJECT = { nbBags: true, transitArea: true, nbProducts: false, departureAt: true, missingTaker: true };
const REFRESH_MS = 60 * 1000;
const CALL_STATUS_WAITING_ANSWER = 2;

// Pourquoi une commande en attente ne sera pas (ou pas tout de suite) donnée
// à un préparateur. Le préparateur en cours a sa colonne « Pris par » et le
// client appelé son téléphone jaune : pas de marque en plus ; les deux autres
// raisons ont une icône barrée.
const blockedReason = (task) => {
  const order = task.linked_order || {};
  // Seule une recherche en cours retient la commande : finie ou quittée, le
  // nom reste sur le bouton mais la commande reprend sa place dans la file.
  if (task.missing_taker && task.missing_taker.active) {
    return { doing: true, title: `${task.missing_taker.name} est sur la recherche de manquants de cette commande.` };
  }
  if (task.nothing_to_search) {
    return { reason: "nothing_to_search", title: "Rien à chercher : il ne manque que des produits ajoutés à l’expédition, aucun préparateur ne la recevra en recherche de manquants." };
  }
  if (!order.transit_area_current) {
    return { reason: "no_transit_area", title: "Pas de zone de dépôt : la commande n’est pas proposée aux préparateurs tant qu’elle n’en a pas." };
  }
  if (order.replace_product_call_status_id === CALL_STATUS_WAITING_ANSWER) {
    return { title: "Le support a appelé le client et attend sa réponse : la commande n’est pas proposée aux préparateurs tant qu’il n’a pas répondu." };
  }
  return null;
};

// Ordre de l'API conservé ; on marque seulement les raisons de blocage.
export const inTreatmentOrder = (tasks) => tasks.map((task) => {
  const reason = blockedReason(task);
  if (reason === null) return task;
  if (reason.doing) return Object.assign({}, task, { queue_doing: true, queue_title: reason.title });
  return Object.assign({}, task, { queue_reason: reason.reason || null, queue_title: reason.title });
});

// Mode passage (chef d'équipe) : quand le pain n'est pas encore arrivé par
// exemple, les préparateurs peuvent passer à la commande suivante ; la commande
// passée reste dans la file pour le suivant. Coupé, chacun reste sur sa commande.
const SkipModeToggle = ({ active, onToggle, busy }) => (
  <button
    type="button"
    className={"btn btn-xs " + (active ? "btn-warning" : "btn-default")}
    disabled={busy}
    onClick={onToggle}
    title={active
      ? "Mode passage activé : les préparateurs peuvent passer une commande en recherche de manquants, elle reste dans la file pour le suivant. Cliquer pour couper."
      : "Mode passage coupé : chaque préparateur reste sur sa commande. Cliquer pour laisser passer à la suivante (pain pas encore arrivé…)."}
  >
    {active
      ? <span><i className="fa fa-forward" /> Passage autorisé · rebloquer les preps</span>
      : <span><i className="fa fa-forward" /> Laisser les preps passer à la suivante</span>}
  </button>
);

SkipModeToggle.propTypes = {
  active: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  busy: PropTypes.bool,
};

// Recharge ce tableau seulement
const RefreshButton = ({ onClick, busy }) => (
  <button
    type="button"
    className="btn btn-default btn-xs"
    disabled={busy}
    onClick={onClick}
    title="Recharger ce tableau"
  >
    <i className={"fa fa-rotate" + (busy ? " fa-spin" : "")} /> Recharger
  </button>
);

RefreshButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  busy: PropTypes.bool,
};

const PackingWaitingListHome = ({ centerId, title_link, enforced }) => {
  const [tasks, setTasks] = useState([]);
  const [skipMode, setSkipMode] = useState({ active: false });
  const [skipBusy, setSkipBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (!centerId) return Promise.resolve();
    setLoading(true);
    const list = api.getWaitingProducts(centerId)
      .then((rows) => setTasks(inTreatmentOrder(rows || [])))
      .catch(() => {});
    const mode = api.getMissingSkipMode(centerId)
      .then((state) => { if (state) setSkipMode(state); })
      .catch(() => {});
    return Promise.all([list, mode]).then(() => setLoading(false));
  }, [centerId]);

  useEffect(() => {
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  const toggleSkipMode = () => {
    setSkipBusy(true);
    api.setMissingSkipMode(centerId, !skipMode.active)
      .then((state) => { if (state) setSkipMode(state); })
      .catch(() => {})
      .then(() => setSkipBusy(false));
  };

  return (
    <PackingList
      tasks={tasks}
      title=" En attente de produits"
      titleRight={(
        <span>
          <RefreshButton onClick={load} busy={loading} />{" "}
          {enforced ? <SkipModeToggle active={!!skipMode.active} onToggle={toggleSkipMode} busy={skipBusy} /> : null}
        </span>
      )}
      title_link={title_link}
      className="waiting-products-list"
      show={SHOW_OBJECT}
    />
  );
};

PackingWaitingListHome.propTypes = {
  centerId: PropTypes.number,
  title_link: PropTypes.object,
  enforced: PropTypes.bool,
};

export default connect((state) => ({
  centerId: extractCenterId(state.user),
  title_link: getTitleLinkMissing(state),
  enforced: isMissingQueueEnforced(state),
}))(PackingWaitingListHome);
